import { __ } from '@wordpress/i18n';
import {
    DataForm,
    ListEmpty,
    ProductAsyncSelect,
    DebouncedInput,
} from '@dokan/components';
import { formatPrice } from '@dokan/utilities';
import { DollarSign, Search } from 'lucide-react';
import ProductsTable from './ProductsTable';
import { twMerge } from 'tailwind-merge';
import { RawHTML } from '@wordpress/element';

const ProductsSection = ( {
    isEditable,
    selectedVendor,
    products,
    productsData,
    handleProductsChange,
} ) => {
    const handleAddProduct = ( selected ) => {
        if ( ! selected ) return;

        const exists = products.some( ( p ) => p.id === selected.value );
        if ( exists ) return;

        const product = selected.raw || {};

        const newProduct = {
            id: selected.value,
            name: selected.label,
            image: product.images?.[ 0 ]?.src || '',
            price: Number( product.price ) || 0,
            permalink: product.permalink || '',
            quantity: 1,
            offer_price: Number( product.price ) || 0,
        };

        handleProductsChange( {
            products: [ ...products, newProduct ],
        } );
    };

    const productsFields = [
        {
            id: 'products',
            type: 'array',
            label: __( 'Products', 'dokan' ),
            Edit: ( props ) => (
                <ProductsTable { ...props } isEditable={ isEditable } />
            ),
        },
        {
            id: 'shipping_cost',
            type: 'number',
            label: __( 'Shipping Cost', 'dokan' ),
            Edit: ( { data: itemData, onChange } ) => (
                <div className="flex justify-between items-center px-7 pt-5 pb-1 border-t border-[#E9E9E9]">
                    <span className="text-sm text-[#575757]">
                        { __( 'Shipping Cost', 'dokan' ) }
                    </span>
                    <div
                        className={ twMerge(
                            'flex items-center max-w-28',
                            itemData.shipping_cost < 0 &&
                                'border border-[#E64B5F] rounded-md overflow-hidden'
                        ) }
                    >
                        <DebouncedInput
                            addOnLeft={
                                dokanWithdrawDashboard?.currency?.symbol || (
                                    <DollarSign size={ 20 } />
                                )
                            }
                            value={ itemData.shipping_cost.toFixed(
                                dokanAdminDashboard?.currency?.precision || 2
                            ) }
                            onChange={ ( value ) =>
                                onChange( {
                                    shipping_cost: parseFloat( value ) || 0,
                                } )
                            }
                            input={ {
                                type: 'number',
                                id: `shipping-cost`,
                                min: '0',
                                step: '1',
                            } }
                            disabled={ ! isEditable }
                            delay={ 1000 }
                        />
                    </div>
                </div>
            ),
        },
        {
            id: 'subtotal',
            type: 'number',
            label: __( 'Subtotal', 'dokan' ),
            readOnly: true,
            render: ( { item: itemData } ) => {
                const subtotal =
                    ( itemData.products || [] ).reduce(
                        ( sum, product ) =>
                            sum + product.offer_price * product.quantity,
                        0
                    ) + ( itemData.shipping_cost || 0 );

                return (
                    <div className="flex justify-between items-center px-7 pb-5">
                        <span className="font-semibold text-sm text-[#575757]">
                            { __( 'Subtotal', 'dokan' ) }
                        </span>
                        <span className="font-semibold text-sm text-[#575757]">
                            <RawHTML>{ formatPrice( subtotal ) }</RawHTML>
                        </span>
                    </div>
                );
            },
        },
    ];

    const productsForm = {
        fields: [
            'products',
            {
                id: 'calculation',
                children: [ 'shipping_cost', 'subtotal' ],
            },
        ],
    };

    return (
        <div className="bg-white rounded-lg shadow">
            <div className="flex items-center justify-between px-6 py-5 leading-none border-b border-[#E9E9E9]">
                <h2 className="text-lg font-semibold">
                    { __( 'Products', 'dokan' ) }
                </h2>
            </div>

            { isEditable && (
                <div className="px-7 pt-7">
                    <ProductAsyncSelect
                        value={ null }
                        onChange={ handleAddProduct }
                        placeholder={ __( 'Search Product', 'dokan' ) }
                        icon={ <Search size={ 16 } /> }
                        iconPosition="left"
                        className={ twMerge(
                            ! selectedVendor?.value && '!rounded-lg'
                        ) }
                        isClearable
                        defaultOptions
                        disabled={ ! selectedVendor?.value }
                        extraQuery={ {
                            author: selectedVendor?.value,
                        } }
                    />
                </div>
            ) }

            { products.length === 0 && (
                <ListEmpty title={ __( 'No products added yet', 'dokan' ) } />
            ) }

            { products.length > 0 && (
                <div className="request-quote-dataform pt-7">
                    <DataForm
                        data={ productsData }
                        form={ productsForm }
                        fields={ productsFields }
                        onChange={ handleProductsChange }
                    />
                </div>
            ) }
        </div>
    );
};

export default ProductsSection;
